// CommunicationsHubPage.js
// Unified dashboard for voice calls, email, chat, and contact communication history

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { 
  Phone, PhoneCall, PhoneOff, PhoneMissed, PhoneIncoming, PhoneOutgoing,
  Mail, MailOpen, Send, Inbox, Archive,
  MessageSquare, MessageCircle, MessagesSquare,
  History, Clock, Calendar, User, Building2, Search,
  Play, Pause, Volume2, VolumeX, Download, Mic, MicOff,
  Video, VideoOff, MoreVertical, RefreshCw, Filter, BarChart3,
  CheckCircle, AlertCircle, XCircle, Loader2, Settings,
  ChevronRight, ChevronDown, ExternalLink, Wifi, WifiOff,
  FileText, Paperclip, Star, StarOff, Trash2, Reply, Forward
} from 'lucide-react';
import { format, formatDistanceToNow, isToday, isYesterday, parseISO } from 'date-fns';
import { toast } from 'sonner';
import api from '../api';
import { AudioWaveformPlayer } from '../components/AudioWaveformPlayer';
import { CallAnalyticsCharts } from '../components/CallAnalyticsCharts';

// Communication Type Icons
const TypeIcons = {
  call: Phone,
  email: Mail,
  chat: MessageSquare,
  meeting: Video
};

// Status Colors
const StatusColors = {
  completed: 'bg-emerald-100 text-emerald-700',
  active: 'bg-blue-100 text-blue-700',
  missed: 'bg-red-100 text-red-700',
  pending: 'bg-amber-100 text-amber-700',
  sent: 'bg-emerald-100 text-emerald-700',
  delivered: 'bg-blue-100 text-blue-700',
  read: 'bg-purple-100 text-purple-700',
  failed: 'bg-red-100 text-red-700'
};

// Stats Card Component
const StatsCard = ({ title, value, icon: Icon, color, trend, onClick, active }) => (
  <Card 
    className={`cursor-pointer transition-all hover:shadow-md ${active ? 'ring-2 ring-purple-500' : ''}`}
    onClick={onClick}
  >
    <CardContent className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className={`text-2xl font-bold ${color}`}>{value}</p>
          {trend && (
            <p className={`text-xs ${trend > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
              {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}% this week
            </p>
          )}
        </div>
        <div className={`h-12 w-12 rounded-full flex items-center justify-center ${color.replace('text-', 'bg-').replace('-600', '-100')}`}>
          <Icon className={`h-6 w-6 ${color}`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

// Contact Quick View
const ContactQuickView = ({ contact, onViewFull }) => {
  if (!contact) return null;
  
  return (
    <Card className="mb-4">
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <Avatar className="h-12 w-12">
            <AvatarImage src={contact.avatar} />
            <AvatarFallback className="bg-purple-100 text-purple-700">
              {contact.first_name?.[0]}{contact.last_name?.[0]}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <h3 className="font-semibold">{contact.first_name} {contact.last_name}</h3>
            <p className="text-sm text-muted-foreground">{contact.company_name}</p>
            <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
              {contact.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{contact.email}</span>}
              {contact.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{contact.phone}</span>}
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => onViewFull(contact)}>
            <ExternalLink className="h-4 w-4 mr-1" /> View Profile
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// Call Log Item
const CallLogItem = ({ call, onPlay, onViewDetails }) => {
  const isIncoming = call.direction === 'inbound';
  const Icon = call.status === 'missed' ? PhoneMissed : 
               call.status === 'completed' ? (isIncoming ? PhoneIncoming : PhoneOutgoing) : Phone;
  
  return (
    <div className="flex items-center gap-4 p-3 border rounded-lg hover:bg-slate-50 transition-colors">
      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
        call.status === 'missed' ? 'bg-red-100' : 'bg-emerald-100'
      }`}>
        <Icon className={`h-5 w-5 ${call.status === 'missed' ? 'text-red-600' : 'text-emerald-600'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="font-medium truncate">{call.contact_name || call.to_number || call.from_number}</p>
          <Badge variant="outline" className={StatusColors[call.status] || 'bg-slate-100'}>
            {call.status}
          </Badge>
        </div>
        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
          <span>{isIncoming ? 'Incoming' : 'Outgoing'}</span>
          <span>•</span>
          <span>{call.duration_seconds ? `${Math.floor(call.duration_seconds / 60)}:${(call.duration_seconds % 60).toString().padStart(2, '0')}` : '--:--'}</span>
          <span>•</span>
          <span>{call.started_at ? formatDistanceToNow(parseISO(call.started_at), { addSuffix: true }) : 'Unknown'}</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        {call.recording_url && (
          <Button variant="ghost" size="sm" onClick={() => onPlay(call)}>
            <Play className="h-4 w-4" />
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={() => onViewDetails(call)}>
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// Email Item
const EmailItem = ({ email, onView, onReply }) => (
  <div className={`flex items-start gap-4 p-3 border rounded-lg hover:bg-slate-50 transition-colors ${!email.read ? 'bg-blue-50/50' : ''}`}>
    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
      {email.read ? <MailOpen className="h-5 w-5 text-blue-600" /> : <Mail className="h-5 w-5 text-blue-600" />}
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <p className={`font-medium truncate ${!email.read ? 'font-semibold' : ''}`}>
          {email.from_name || email.from_email}
        </p>
        {email.starred && <Star className="h-4 w-4 text-amber-500 fill-amber-500" />}
      </div>
      <p className="text-sm font-medium truncate">{email.subject}</p>
      <p className="text-xs text-muted-foreground truncate mt-1">{email.preview || email.body?.substring(0, 100)}</p>
      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-2">
        <span>{email.sent_at ? formatDistanceToNow(parseISO(email.sent_at), { addSuffix: true }) : ''}</span>
        {email.attachments?.length > 0 && (
          <span className="flex items-center gap-1"><Paperclip className="h-3 w-3" />{email.attachments.length}</span>
        )}
      </div>
    </div>
    <div className="flex items-center gap-1">
      <Button variant="ghost" size="sm" onClick={() => onReply(email)}>
        <Reply className="h-4 w-4" />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => onView(email)}>
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  </div>
);

// Chat Session Item
const ChatSessionItem = ({ session, onOpen }) => (
  <div 
    className={`flex items-center gap-4 p-3 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors ${session.unread_count > 0 ? 'bg-purple-50/50' : ''}`}
    onClick={() => onOpen(session)}
  >
    <Avatar className="h-10 w-10">
      <AvatarFallback className="bg-purple-100 text-purple-700">
        {session.contact_name?.[0] || session.visitor_name?.[0] || '?'}
      </AvatarFallback>
    </Avatar>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <p className={`font-medium truncate ${session.unread_count > 0 ? 'font-semibold' : ''}`}>
          {session.contact_name || session.visitor_name || 'Anonymous'}
        </p>
        {session.status === 'active' && (
          <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
        )}
      </div>
      <p className="text-sm text-muted-foreground truncate">{session.last_message}</p>
      <p className="text-xs text-muted-foreground mt-1">
        {session.last_message_at ? formatDistanceToNow(parseISO(session.last_message_at), { addSuffix: true }) : ''}
      </p>
    </div>
    {session.unread_count > 0 && (
      <Badge className="bg-purple-600 text-white">{session.unread_count}</Badge>
    )}
  </div>
);

// Unified Timeline Item
const TimelineItem = ({ item }) => {
  const Icon = TypeIcons[item.type] || History;
  const colorMap = {
    call: 'bg-emerald-100 text-emerald-600',
    email: 'bg-blue-100 text-blue-600',
    chat: 'bg-purple-100 text-purple-600',
    meeting: 'bg-amber-100 text-amber-600'
  };
  
  return (
    <div className="flex gap-4 pb-4">
      <div className="flex flex-col items-center">
        <div className={`h-10 w-10 rounded-full flex items-center justify-center ${colorMap[item.type] || 'bg-slate-100'}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="w-px flex-1 bg-slate-200 mt-2" />
      </div>
      <div className="flex-1 pb-4">
        <div className="flex items-center gap-2 mb-1">
          <Badge variant="outline" className="text-xs capitalize">{item.type}</Badge>
          <span className="text-xs text-muted-foreground">
            {item.timestamp ? formatDistanceToNow(parseISO(item.timestamp), { addSuffix: true }) : ''}
          </span>
        </div>
        <p className="font-medium">{item.title}</p>
        <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
        {item.metadata && (
          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
            {item.metadata.duration && <span>Duration: {item.metadata.duration}</span>}
            {item.metadata.status && <span>Status: {item.metadata.status}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

// New Call Dialog
const NewCallDialog = ({ open, onOpenChange, contacts, onCall }) => {
  const [selectedContact, setSelectedContact] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isDialing, setIsDialing] = useState(false);
  
  const handleCall = async () => {
    if (!phoneNumber && !selectedContact) {
      toast.error('Please enter a phone number or select a contact');
      return;
    }
    
    setIsDialing(true);
    try {
      await onCall(selectedContact?.phone || phoneNumber, selectedContact);
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to initiate call');
    } finally {
      setIsDialing(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-emerald-600" />
            New Call
          </DialogTitle>
          <DialogDescription>
            Enter a phone number or select a contact to call
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div>
            <label className="text-sm font-medium mb-2 block">Phone Number</label>
            <Input
              placeholder="+1 (555) 123-4567"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
            />
          </div>
          
          <div className="text-center text-sm text-muted-foreground">- or -</div>
          
          <div>
            <label className="text-sm font-medium mb-2 block">Select Contact</label>
            <Select value={selectedContact?.id} onValueChange={(id) => {
              const contact = contacts.find(c => c.id === id);
              setSelectedContact(contact);
              if (contact?.phone) setPhoneNumber(contact.phone);
            }}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a contact..." />
              </SelectTrigger>
              <SelectContent>
                {contacts.map(contact => (
                  <SelectItem key={contact.id} value={contact.id}>
                    {contact.first_name} {contact.last_name} - {contact.phone || 'No phone'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={handleCall} 
            disabled={isDialing || (!phoneNumber && !selectedContact)}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {isDialing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Phone className="h-4 w-4 mr-2" />}
            Call Now
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Compose Email Dialog
const ComposeEmailDialog = ({ open, onOpenChange, contacts, templates, onSend }) => {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [isSending, setIsSending] = useState(false);
  
  const handleSend = async () => {
    if (!to || !subject || !body) {
      toast.error('Please fill all required fields');
      return;
    }
    
    setIsSending(true);
    try {
      await onSend({ to, subject, body });
      toast.success('Email sent successfully!');
      onOpenChange(false);
      setTo(''); setSubject(''); setBody('');
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setIsSending(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-blue-600" />
            Compose Email
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">To</label>
              <Input
                placeholder="recipient@example.com"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-2 block">Template (Optional)</label>
              <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                <SelectTrigger>
                  <SelectValue placeholder="Use template..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.type} value={t.type}>{t.type.replace('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div>
            <label className="text-sm font-medium mb-2 block">Subject</label>
            <Input
              placeholder="Email subject..."
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
          </div>
          
          <div>
            <label className="text-sm font-medium mb-2 block">Message</label>
            <Textarea
              placeholder="Write your message..."
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={8}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button 
            onClick={handleSend} 
            disabled={isSending || !to || !subject || !body}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
            Send Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Main Component
const CommunicationsHubPage = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // Data states
  const [callLogs, setCallLogs] = useState([]);
  const [emails, setEmails] = useState([]);
  const [chatSessions, setChatSessions] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  
  // Service status
  const [twilioStatus, setTwilioStatus] = useState({ configured: false });
  
  // Recording player state
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [showRecordingPlayer, setShowRecordingPlayer] = useState(false);
  const [resendStatus, setResendStatus] = useState({ configured: false });
  
  // Dialog states
  const [showNewCallDialog, setShowNewCallDialog] = useState(false);
  const [showComposeEmailDialog, setShowComposeEmailDialog] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  
  // Stats
  const stats = useMemo(() => ({
    totalCalls: callLogs.length,
    missedCalls: callLogs.filter(c => c.status === 'missed').length,
    totalEmails: emails.length,
    unreadEmails: emails.filter(e => !e.read).length,
    activeChats: chatSessions.filter(s => s.status === 'active').length,
    totalChats: chatSessions.length
  }), [callLogs, emails, chatSessions]);
  
  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Fetch service status
        const [twilioRes, resendRes] = await Promise.all([
          api.get('/voice/status').catch(() => ({ data: { configured: false } })),
          api.get('/email/status').catch(() => ({ data: { configured: false } }))
        ]);
        setTwilioStatus(twilioRes.data);
        setResendStatus(resendRes.data);
        
        // Fetch call logs
        if (twilioRes.data.configured) {
          const callsRes = await api.get('/voice/calls').catch(() => ({ data: { calls: [] } }));
          setCallLogs(callsRes.data.calls || []);
        }
        
        // Fetch email logs
        const emailLogsRes = await api.get('/email/logs').catch(() => ({ data: { logs: [] } }));
        setEmails(emailLogsRes.data.logs || []);
        
        // Fetch email templates
        const templatesRes = await api.get('/email/templates').catch(() => ({ data: { templates: [] } }));
        setEmailTemplates(templatesRes.data.templates || []);
        
        // Fetch chat sessions
        const chatsRes = await api.get('/chat/sessions').catch(() => ({ data: { sessions: [] } }));
        setChatSessions(chatsRes.data.sessions || []);
        
        // Fetch contacts for dialogs
        const contactsRes = await api.get('/contacts?limit=100').catch(() => ({ data: [] }));
        setContacts(Array.isArray(contactsRes.data) ? contactsRes.data : contactsRes.data.contacts || []);
        
        // Build unified timeline
        buildTimeline();
        
      } catch (error) {
        console.error('Failed to fetch communications data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  const buildTimeline = useCallback(() => {
    const items = [];
    
    // Add calls
    callLogs.forEach(call => {
      items.push({
        type: 'call',
        timestamp: call.started_at,
        title: `${call.direction === 'inbound' ? 'Incoming' : 'Outgoing'} call ${call.status === 'completed' ? 'completed' : call.status}`,
        description: `${call.contact_name || call.to_number || call.from_number}`,
        metadata: {
          duration: call.duration_seconds ? `${Math.floor(call.duration_seconds / 60)}m ${call.duration_seconds % 60}s` : null,
          status: call.status
        }
      });
    });
    
    // Add emails
    emails.forEach(email => {
      items.push({
        type: 'email',
        timestamp: email.sent_at,
        title: email.subject,
        description: `To: ${email.recipient_email}`,
        metadata: { status: email.status }
      });
    });
    
    // Sort by timestamp
    items.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    setTimeline(items.slice(0, 50));
  }, [callLogs, emails]);
  
  useEffect(() => {
    buildTimeline();
  }, [callLogs, emails, buildTimeline]);
  
  // Handlers
  const handlePlayRecording = useCallback((call) => {
    if (call.recording_url) {
      setSelectedRecording(call);
      setShowRecordingPlayer(true);
    } else {
      toast.error('No recording available for this call');
    }
  }, []);
  
  const handleMakeCall = async (phoneNumber, contact) => {
    try {
      const res = await api.post('/voice/outbound-call', {
        to_number: phoneNumber,
        contact_id: contact?.id,
        contact_name: contact ? `${contact.first_name} ${contact.last_name}` : null,
        user_id: 'current_user',
        record: true
      });
      
      if (res.data.success) {
        toast.success('Call initiated!');
        // Refresh call logs
        const callsRes = await api.get('/voice/calls');
        setCallLogs(callsRes.data.calls || []);
      }
    } catch (error) {
      throw error;
    }
  };
  
  const handleSendEmail = async ({ to, subject, body }) => {
    const res = await api.post('/email/send', {
      recipient_email: to,
      subject,
      html_content: `<div style="font-family: Arial, sans-serif;">${body.replace(/\n/g, '<br>')}</div>`
    });
    
    if (res.data.success) {
      // Refresh email logs
      const emailLogsRes = await api.get('/email/logs');
      setEmails(emailLogsRes.data.logs || []);
    }
  };
  
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-purple-500 to-violet-600 flex items-center justify-center">
              <MessagesSquare className="h-6 w-6 text-white" />
            </div>
            Communications Hub
          </h1>
          <p className="text-muted-foreground mt-1">
            Unified dashboard for voice calls, email, and chat
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => window.location.reload()}>
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button 
            onClick={() => setShowNewCallDialog(true)}
            className="bg-emerald-600 hover:bg-emerald-700"
            disabled={!twilioStatus.configured}
          >
            <Phone className="h-4 w-4 mr-2" /> New Call
          </Button>
          <Button 
            onClick={() => setShowComposeEmailDialog(true)}
            className="bg-blue-600 hover:bg-blue-700"
            disabled={!resendStatus.configured}
          >
            <Mail className="h-4 w-4 mr-2" /> Compose
          </Button>
        </div>
      </div>
      
      {/* Service Status Alerts */}
      {(!twilioStatus.configured || !resendStatus.configured) && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Service Configuration Required</p>
                <div className="text-sm text-amber-700 mt-1 space-y-1">
                  {!twilioStatus.configured && (
                    <p>• <strong>Twilio Voice:</strong> Add TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN, TWILIO_PHONE_NUMBER to .env</p>
                  )}
                  {!resendStatus.configured && (
                    <p>• <strong>Resend Email:</strong> Add RESEND_API_KEY to .env</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatsCard 
          title="Total Calls" 
          value={stats.totalCalls} 
          icon={Phone} 
          color="text-emerald-600"
        />
        <StatsCard 
          title="Missed Calls" 
          value={stats.missedCalls} 
          icon={PhoneMissed} 
          color="text-red-600"
        />
        <StatsCard 
          title="Emails Sent" 
          value={stats.totalEmails} 
          icon={Mail} 
          color="text-blue-600"
        />
        <StatsCard 
          title="Unread Emails" 
          value={stats.unreadEmails} 
          icon={MailOpen} 
          color="text-amber-600"
        />
        <StatsCard 
          title="Active Chats" 
          value={stats.activeChats} 
          icon={MessageCircle} 
          color="text-purple-600"
        />
        <StatsCard 
          title="Total Chats" 
          value={stats.totalChats} 
          icon={MessagesSquare} 
          color="text-slate-600"
        />
      </div>
      
      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-6">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <History className="h-4 w-4" /> Overview
          </TabsTrigger>
          <TabsTrigger value="calls" className="flex items-center gap-2">
            <Phone className="h-4 w-4" /> Voice Calls
          </TabsTrigger>
          <TabsTrigger value="analytics" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" /> Analytics
          </TabsTrigger>
          <TabsTrigger value="emails" className="flex items-center gap-2">
            <Mail className="h-4 w-4" /> Email
          </TabsTrigger>
          <TabsTrigger value="chat" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" /> Live Chat
          </TabsTrigger>
          <TabsTrigger value="timeline" className="flex items-center gap-2">
            <Clock className="h-4 w-4" /> Timeline
          </TabsTrigger>
        </TabsList>
        
        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Recent Calls */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Phone className="h-5 w-5 text-emerald-600" /> Recent Calls
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  <div className="space-y-2">
                    {callLogs.length > 0 ? (
                      callLogs.slice(0, 5).map(call => (
                        <CallLogItem key={call.call_sid || call.id} call={call} onPlay={handlePlayRecording} onViewDetails={() => {}} />
                      ))
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Phone className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>{twilioStatus.configured ? 'No calls yet' : 'Configure Twilio to make calls'}</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
            
            {/* Recent Emails */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Mail className="h-5 w-5 text-blue-600" /> Recent Emails
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  <div className="space-y-2">
                    {emails.length > 0 ? (
                      emails.slice(0, 5).map(email => (
                        <EmailItem key={email.id || email.email_id} email={email} onView={() => {}} onReply={() => {}} />
                      ))
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Mail className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>{resendStatus.configured ? 'No emails sent yet' : 'Configure Resend to send emails'}</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
            
            {/* Active Chats */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-purple-600" /> Active Chats
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[300px]">
                  <div className="space-y-2">
                    {chatSessions.length > 0 ? (
                      chatSessions.filter(s => s.status === 'active').slice(0, 5).map(session => (
                        <ChatSessionItem key={session.id} session={session} onOpen={() => {}} />
                      ))
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No active chats</p>
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
        
        {/* Voice Calls Tab */}
        <TabsContent value="calls" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Phone className="h-5 w-5 text-emerald-600" /> Call History
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Input 
                    placeholder="Search calls..." 
                    className="w-64"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <Button 
                    onClick={() => setShowNewCallDialog(true)} 
                    disabled={!twilioStatus.configured}
                  >
                    <Phone className="h-4 w-4 mr-2" /> New Call
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-2">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
                    </div>
                  ) : callLogs.length > 0 ? (
                    callLogs.map(call => (
                      <CallLogItem key={call.call_sid || call.id} call={call} onPlay={handlePlayRecording} onViewDetails={() => {}} />
                    ))
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <Phone className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <h3 className="font-medium mb-1">No Call History</h3>
                      <p className="text-sm">{twilioStatus.configured ? 'Make your first call to get started' : 'Configure Twilio to enable voice calling'}</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Analytics Tab */}
        <TabsContent value="analytics" className="mt-6">
          <CallAnalyticsCharts calls={callLogs} dateRange="7d" />
        </TabsContent>
        
        {/* Email Tab */}
        <TabsContent value="emails" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-blue-600" /> Email Log
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Input 
                    placeholder="Search emails..." 
                    className="w-64"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <Button 
                    onClick={() => setShowComposeEmailDialog(true)}
                    disabled={!resendStatus.configured}
                  >
                    <Mail className="h-4 w-4 mr-2" /> Compose
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-2">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                    </div>
                  ) : emails.length > 0 ? (
                    emails.map(email => (
                      <EmailItem key={email.id || email.email_id} email={email} onView={() => {}} onReply={() => {}} />
                    ))
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <Mail className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <h3 className="font-medium mb-1">No Emails Yet</h3>
                      <p className="text-sm">{resendStatus.configured ? 'Send your first email to get started' : 'Configure Resend to enable email sending'}</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Chat Tab */}
        <TabsContent value="chat" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-purple-600" /> Chat Sessions
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                <div className="space-y-2">
                  {chatSessions.length > 0 ? (
                    chatSessions.map(session => (
                      <ChatSessionItem key={session.id} session={session} onOpen={() => {}} />
                    ))
                  ) : (
                    <div className="text-center py-12 text-muted-foreground">
                      <MessageSquare className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <h3 className="font-medium mb-1">No Chat Sessions</h3>
                      <p className="text-sm">Chat sessions from your website widget will appear here</p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Timeline Tab */}
        <TabsContent value="timeline" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-slate-600" /> Communication Timeline
              </CardTitle>
              <CardDescription>
                Unified view of all communications across channels
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[500px]">
                {timeline.length > 0 ? (
                  <div className="pl-2">
                    {timeline.map((item, idx) => (
                      <TimelineItem key={idx} item={item} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <History className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <h3 className="font-medium mb-1">No Activity Yet</h3>
                    <p className="text-sm">Start making calls or sending emails to see your timeline</p>
                  </div>
                )}
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* Dialogs */}
      <NewCallDialog 
        open={showNewCallDialog} 
        onOpenChange={setShowNewCallDialog}
        contacts={contacts}
        onCall={handleMakeCall}
      />
      
      <ComposeEmailDialog 
        open={showComposeEmailDialog}
        onOpenChange={setShowComposeEmailDialog}
        contacts={contacts}
        templates={emailTemplates}
        onSend={handleSendEmail}
      />
      
      {/* Recording Player Dialog */}
      <Dialog open={showRecordingPlayer} onOpenChange={setShowRecordingPlayer}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden">
          {selectedRecording && (
            <AudioWaveformPlayer
              src={selectedRecording.recording_url}
              title={selectedRecording.contact_name || 'Call Recording'}
              subtitle={selectedRecording.to_number || selectedRecording.from_number}
              callData={selectedRecording}
              showDownload={true}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommunicationsHubPage;
